
import React, { useState } from 'react';
import { FlatList, TouchableOpacity, Image } from "react-native"
import { Icons } from '../../Components';
import type { AccountProps, MyPlayerProps, PlayerBalanceProps } from '../../types';
import { WalletHelpers } from '../api';
import { Button, Text, View } from '../../Components/Themed';
import { useColors } from '../../constants/useColors';

type WalletActionSelectorProps ={
    player:MyPlayerProps,
    player_balance:PlayerBalanceProps,
    accounts:AccountProps[],
    onClose: () => void,
    walkthrough_steps:any[],
    onActionSelect:(action:any, account?:AccountProps) => void,
    onStartWalkthrough:(walkthrough:any, account?:AccountProps) => void
}



const WalletActionSelector = ({ player, walkthrough_steps, player_balance, accounts, onClose, onActionSelect, onStartWalkthrough }: WalletActionSelectorProps) => {
    const Colors = useColors();
    const [ show_steps, setShowSteps ] = useState(false);
    const [ width, setWidth ] = useState(0);

    const sections = [
        { 
            section: 'accounts', label:'Account Actions', actions : [
                { 
                    action :'my_accounts', label: "View My Accounts", icon:<Icons.ListIcon color={Colors.text.h1} size={12}/>, description: 'Manage your connected accounts.  Transact, snooze, set limits or remove an existing account.'
                },
                { 
                    action :'wallet_settings', label: "View My Settings", icon:<Icons.SettingsIcon color={Colors.text.h1} size={20}/>, description: 'View your current settings including your deposit and withdraw limits.'
                },
                
            ] 
        },
        { 
            section: 'transact', label:'Transact', actions : [
                { 
                    action :'order_history', label: "View Order History", icon:<Icons.TransactionIcon size={20} color={Colors.text.h1} />, description: 'View and manage past orders made on BettorEdge'
                },
                { 
                    action :'deposit', label: "Make A Deposit", icon:<Icons.DepositIcon size={20} color={Colors.text.h1} />, description: 'Fund your BettorEdge real-money balance by making a deposit from an existing account or new one.'
                },
                { 
                    action :'withdraw', label: "Withdraw Funds (FREE)", icon:<Icons.WithdrawIcon size={20} color={Colors.text.h1} />, description: 'Move funds from your BettorEdge real-money balance to a connected ACH account. (Up to 3 biz days)'
                },
                { 
                    action :'instant_withdraw', label: "Instantly Withdraw Funds", icon:<Icons.WithdrawIcon size={20} color={Colors.text.h1} />, description: 'Instantly Move funds from your BettorEdge real-money balance to a connected ACH account.'
                },
                
            ] 
        }
    ]

    const handleActionSelect = (action:any) => {
        switch(action.action){
            case 'deposit':
                let default_account = accounts.find(a => a.default_account)
                if(!default_account){ default_account = accounts.find(a => a.account_type == 'paypal')}
                return onActionSelect(action, default_account)
            case 'withdraw':
                let withdraw_account = accounts.find(a => !a.ibt_placeholder && a.account_type == 'ach' && a.status == 'active');
                if(!withdraw_account){ return onActionSelect({ action: 'ach_add' }) }
                return onActionSelect(action, withdraw_account)
            case 'instant_withdraw':
                let instant_withdraw_account = accounts.find(a => !a.ibt_placeholder && a.status == 'active' && a.account_type == 'ach' && a.ach_detail?.ach_type == 'ibf');
                if(instant_withdraw_account){ return onActionSelect(action, instant_withdraw_account) }
                //Ok lets get the ibt_placeholder one!
                let ibt_placeholder = accounts.find(a => a.ibt_placeholder);
                if(ibt_placeholder){ return onActionSelect(action, ibt_placeholder) }
                return //Dont do anything if we dont have an ibt acccount possible
            default: onActionSelect(action)
        }
    }

    const renderActions = (data:{ item:any, index:number }) => {
        return (
            <Button style={{ padding:10, flexDirection:'row', alignItems:'center', borderBottomWidth:1, borderBottomColor:Colors.borders.light }}
            onPress={() => handleActionSelect(data.item)}>

                <View style={{ flex:1, marginRight:10 }}>
                    <View style={{ flexDirection:'row', alignItems:'center', padding:5 }}>
                        {data.item.icon?
                            data.item.icon
                        :<></>}
                        <Text style={{ marginLeft:5 }} size={16} theme='h1'>{data.item.label}</Text>
                    </View>
                    <Text style={{ padding:5 }} size={14} theme='description'>{data.item.description}</Text>
                </View>
                <Icons.ChevronIcon direction='right' color={Colors.text.h1} size={12} />
            </Button>
        )
    }

    const renderSections = (data: { item:any, index:number }) => {
        return (
            <View style={{ marginTop:10 }}>
                <View type='header' style={{ padding:15 }}>
                    <Text theme='h1'>{data.item.label.toUpperCase()}</Text>
                </View>
                <View style={{ padding:5 }}>
                    <FlatList
                        key='actions'
                        data={data.item.actions}
                        renderItem={renderActions}
                        keyExtractor={(item) => item.action}
                    />
                </View>
                
            </View>
        )
    }

    const handleStepSelect = (step:any, index:number) => {
        switch(step.step){
            case 'deposit':
                let default_account = accounts.find(a => a.default_account)
                if(!default_account){ default_account = accounts.find(a => a.account_type == 'paypal') }
                if(!default_account){ return }
                return onStartWalkthrough({ enabled:true, steps: walkthrough_steps, active_step: step.step, next_step: walkthrough_steps[index + 1]?.step}, default_account)
            default:
                return onStartWalkthrough({ enabled:true, steps: walkthrough_steps, active_step: step.step, next_step: walkthrough_steps[index + 1]?.step})
        }
        
    }

    const renderSteps = (data: { item:any, index:number }) => {
        return (
            <TouchableOpacity
                style={{ margin:5, padding:10, flexDirection:'row', alignItems:'center', borderWidth:1, borderColor:Colors.borders.light, borderRadius:8 }}
                onPress={() => handleStepSelect(data.item, data.index)}>
                <Icons.CheckCirlceIcon size={14} color={data.item.complete?Colors.text.success:Colors.text.success}/>
                <Text style={{ flex:1, marginLeft:10 }} theme='description'>{data.item.label}</Text>
                <Icons.ChevronIcon direction='right' size={8} color={Colors.text.h1} />
            </TouchableOpacity>
        )
    }

    const pct_complete = walkthrough_steps.filter(s => s.complete).length / walkthrough_steps.length
    
    return (
        <View style={{ flex:1 }} onLayout={(ev) => {
            const { width } = ev.nativeEvent.layout
            setWidth(width)
        }}>
            <View type='header' style={{ padding:10, flexDirection:'row', alignItems:'center' }}>
                <Image
                    source={{ uri: player.profile_pic }}
                    style={{ height:60, width:60, borderRadius:4 }}
                    resizeMode='cover'
                />
                <View transparent style={{ flex:1, marginLeft:15 }}>
                    <Text size={20} theme='h1'>My Wallet Balances</Text>
                    <View transparent style={{ flexDirection:'row', marginTop:5 }}>
                        <View float style={{ margin:3, padding:8, borderRadius:8 }}>
                            <Text size={14} theme='h1'>${WalletHelpers.formatBalance(player_balance.balance)}</Text>
                        </View>
                        <View float style={{ margin:3, padding:8, borderRadius:8 }}>
                            <Text size={14} theme='h1'>P{WalletHelpers.formatBalance(player_balance.promo_balance)}</Text>
                        </View>
                        <View float style={{ margin:3, padding:8, borderRadius:8 }}>
                            <Text size={14} theme='h1'>E{WalletHelpers.formatBalance(player_balance.free_market_balance)}</Text>
                        </View>
                    </View>
                </View>
            </View>
            <View style={{ flex:1 }}>
                {pct_complete < 1 ?
                <View style={{ padding:10 }}>
                    <View>
                        <TouchableOpacity style={{ flexDirection:'row', alignItems:'center', padding:10 }} onPress={() => setShowSteps(!show_steps)}>
                            <View style={{ flex:1, marginRight:10 }}>
                                <Text theme='h1'>Setup your wallet</Text>
                                <Text style={{ marginTop:3 }} theme='description'>Complete all the steps below to have full access to move funds in and out of BettorEdge</Text>
                            </View>
                            <Icons.ChevronIcon direction={show_steps ? 'up' : 'down'} color={Colors.text.h1} size={10} />
                        </TouchableOpacity>
                        <View style={{ flexDirection:'row', justifyContent:'center', alignItems:'center', }}>
                            <Text size={14} weight='bold'>{(pct_complete * 100).toFixed()}%</Text>
                            <View style={{ marginLeft:20, height:10, width:width - 100, borderRadius:15 }}>
                                <View style={{ position:'absolute', top:0, left:0, bottom:0, width: (width-100)*pct_complete, backgroundColor:Colors.text.success, borderRadius:15 }} />
                            </View>
                        </View>
                    </View>
                    {show_steps ?
                    <View style={{marginTop:5, borderTopWidth:1, borderColor:Colors.borders.light }}>
                        <FlatList
                            data={walkthrough_steps}
                            renderItem={renderSteps}
                            keyExtractor={(item) => item.step}
                        />
                    </View>
                    :<></>}
                </View>
                :<></>}
                <View type='body' style={{ flex:1, marginTop:5 }}>
                    <FlatList
                        key={'wallet_sections'}
                        data={sections}
                        renderItem={renderSections}
                        keyExtractor={(item) => item.section}
                    />
                </View>
            </View>
            <View nativeID='action_row' style={{ flexDirection:'row', alignItems:'center', padding:20 }}>
                <Button
                    title='CLOSE'
                    style={{ flex:1 }}
                    type='close'
                    onPress={() => onClose()}
                />
            </View>
        </View>
    )
}

export default WalletActionSelector